import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import {
  FileText,
  Loader2,
  Plus,
  Trash2,
  PoundSterling,
  Receipt,
  Mail,
  Eye,
} from 'lucide-react';
import { toast } from 'sonner';
import { EmailPreviewDialog } from './EmailPreviewDialog';
import { replaceCustomerVars } from '../lib/emailEditor';

export const XeroInvoiceDialog = ({ open, onOpenChange, customer, onInvoiceCreated }) => {
  const [docType, setDocType] = useState('invoice');
  const [invoiceType, setInvoiceType] = useState('deposit');
  const [brandingThemes, setBrandingThemes] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [docTemplates, setDocTemplates] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [emailTemplateId, setEmailTemplateId] = useState('default');
  const [taxRates, setTaxRates] = useState([]);
  const [defaultTaxType, setDefaultTaxType] = useState('ZERORATEDOUTPUT');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [selectedTheme, setSelectedTheme] = useState('');
  const [dueDays, setDueDays] = useState(30);
  const [reference, setReference] = useState('');
  const [lineItems, setLineItems] = useState([]);
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [emailPreviewOpen, setEmailPreviewOpen] = useState(false);

  const depositAmount = parseFloat(customer?.deposit_amount) || 0;
  const finalBillAmount = parseFloat(customer?.final_bill_amount) || 0;
  const name = `${customer?.first_name || ''} ${customer?.last_name || ''}`.trim();

  useEffect(() => {
    if (open) {
      loadXeroData();
      setDocType('invoice');
      setInvoiceType('deposit');
      setDueDays(30);
      setReference('');
      setSelectedTheme('');
      setSelectedTemplate('');
      setLineItems([{
        description: `Solar Installation Deposit - ${name}`,
        quantity: 1,
        unit_amount: depositAmount || 0,
        account_code: '200',
      }]);
    }
  }, [open]);

  const loadXeroData = async () => {
    setLoading(true);
    try {
      const [themesRes, accountsRes, templatesRes, taxRes, emailTemplatesRes] = await Promise.all([
        api.get('/xero/branding-themes').catch(() => ({ data: [] })),
        api.get('/xero/accounts').catch(() => ({ data: [] })),
        api.get('/xero/doc-templates').catch(() => ({ data: [] })),
        api.get('/xero/tax-rates').catch(() => ({ data: [] })),
        api.get('/email-templates').catch(() => ({ data: [] })),
      ]);
      setBrandingThemes(themesRes.data || []);
      setAccounts(accountsRes.data || []);
      setDocTemplates(templatesRes.data || []);
      setTaxRates(taxRes.data || []);
      setEmailTemplates(emailTemplatesRes.data || []);
    } catch {
      // Defaults will be used
    }
    setLoading(false);
  };

  const handleDocTypeChange = (type) => {
    setDocType(type);
    setSelectedTemplate('');
    if (type === 'quote') {
      setInvoiceType('custom');
      setLineItems([{
        description: `Solar Installation Quote - ${name}`,
        quantity: 1,
        unit_amount: parseFloat(customer?.system_total_cost) || 0,
        account_code: '200',
      }]);
    } else {
      setInvoiceType('deposit');
      setLineItems([{
        description: `Solar Installation Deposit - ${name}`,
        quantity: 1,
        unit_amount: depositAmount || 0,
        account_code: '200',
      }]);
    }
  };

  const handleTemplateSelect = (templateId) => {
    setSelectedTemplate(templateId);
    if (templateId === 'none') {
      setSelectedTemplate('');
      return;
    }
    const tmpl = docTemplates.find(t => t.id === templateId);
    if (tmpl) {
      if (tmpl.line_items?.length > 0) {
        // Replace variables in descriptions
        const replaceVars = (text) => {
          return (text || '')
            .replace(/\{\{name\}\}/g, name)
            .replace(/\{\{first_name\}\}/g, customer?.first_name || '')
            .replace(/\{\{last_name\}\}/g, customer?.last_name || '')
            .replace(/\{\{address\}\}/g, customer?.address || '')
            .replace(/\{\{system_total_cost\}\}/g, String(customer?.system_total_cost || ''))
            .replace(/\{\{deposit_amount\}\}/g, String(customer?.deposit_amount || ''))
            .replace(/\{\{system_size\}\}/g, String(customer?.system_size || ''))
            .replace(/\{\{panel_manufacturer\}\}/g, customer?.panel_manufacturer || '')
            .replace(/\{\{inverter_manufacturer\}\}/g, customer?.inverter_manufacturer || '')
            .replace(/\{\{battery_manufacturer\}\}/g, customer?.battery_manufacturer || '')
            .replace(/\{\{panel_quantity\}\}/g, String(customer?.panel_quantity || ''));
        };
        setLineItems(tmpl.line_items.map(li => ({
          ...li,
          description: replaceVars(li.description),
        })));
      }
      if (tmpl.reference_template) {
        setReference(tmpl.reference_template
          .replace(/\{\{name\}\}/g, name)
          .replace(/\{\{address\}\}/g, customer?.address || ''));
      }
      if (tmpl.due_days) setDueDays(tmpl.due_days);
      if (tmpl.branding_theme_id) setSelectedTheme(tmpl.branding_theme_id);
    }
  };

  const handleTypeChange = (type) => {
    setInvoiceType(type);
    switch (type) {
      case 'deposit':
        setLineItems([{
          description: `Solar Installation Deposit - ${name}`,
          quantity: 1,
          unit_amount: depositAmount || 0,
          account_code: '200',
        }]);
        break;
      case 'final_payment':
        setLineItems([{
          description: `Solar Installation Final Payment - ${name}`,
          quantity: 1,
          unit_amount: finalBillAmount || 0,
          account_code: '200',
        }]);
        break;
      case 'custom':
        setLineItems([{ description: '', quantity: 1, unit_amount: 0, account_code: '200' }]);
        break;
    }
  };

  const updateLineItem = (index, field, value) => {
    setLineItems(prev => prev.map((item, i) =>
      i === index ? { ...item, [field]: value } : item
    ));
  };

  const addLineItem = () => {
    setLineItems(prev => [...prev, { description: '', quantity: 1, unit_amount: 0, account_code: '200' }]);
  };

  const removeLineItem = (index) => {
    if (lineItems.length === 1) return;
    setLineItems(prev => prev.filter((_, i) => i !== index));
  };

  const total = lineItems.reduce((sum, item) =>
    sum + (parseFloat(item.unit_amount) || 0) * (parseFloat(item.quantity) || 0), 0
  );

  const handleCreate = async (mode = 'draft') => {
    // mode: 'draft' | 'send'
    if (lineItems.some(li => !li.description?.trim())) {
      toast.error('Please add a description for all line items');
      return;
    }
    if (total <= 0) {
      toast.error('Total must be greater than £0');
      return;
    }

    const isQuoteDoc = docType === 'quote';
    const status = mode === 'send'
      ? (isQuoteDoc ? 'SENT' : 'AUTHORISED')
      : 'DRAFT';

    setCreating(true);
    try {
      const payload = {
        description: lineItems[0].description,
        quantity: lineItems[0].quantity,
        unit_amount: lineItems[0].unit_amount,
        account_code: lineItems[0].account_code,
        due_days: dueDays,
        reference: reference || `Helios - ${name}`,
        branding_theme_id: selectedTheme,
        invoice_type: invoiceType,
        doc_type: docType,
        template_id: selectedTemplate,
        status,
        email_to_customer: mode === 'send' && !isQuoteDoc,  // Xero only emails invoices, not quotes via this endpoint
        tax_type: defaultTaxType || '',
        email_template_id: emailTemplateId !== 'default' ? emailTemplateId : '',
        line_items: lineItems.map(li => ({
          description: li.description,
          quantity: parseFloat(li.quantity) || 1,
          unit_amount: parseFloat(li.unit_amount) || 0,
          account_code: li.account_code || '200',
        })),
      };

      const res = await api.post(`/customers/${customer.id}/xero/invoice`, payload);
      const labelDoc = docType === 'quote' ? 'Quote' : 'Invoice';
      let msg;
      if (mode === 'send') {
        if (res.data?.emailed) {
          msg = `${labelDoc} ${res.data.invoice_number} created & emailed to customer via Xero`;
        } else if (res.data?.email_error) {
          msg = `${labelDoc} ${res.data.invoice_number} created (${status}) — Xero email failed: ${res.data.email_error}`;
        } else {
          msg = `${labelDoc} ${res.data.invoice_number} created (${status})`;
        }
      } else {
        msg = `${labelDoc} ${res.data.invoice_number} saved as Draft in Xero`;
      }
      toast.success(msg);
      onOpenChange(false);
      onInvoiceCreated?.();
    } catch (err) {
      const apiMsg = err.response?.data?.detail || `Failed to create ${docType}`;
      toast.error(apiMsg);
    } finally {
      setCreating(false);
    }
  };

  const filteredTemplates = docTemplates.filter(t => t.doc_type === docType);
  const isQuote = docType === 'quote';
  const label = isQuote ? 'Quote' : 'Invoice';

  // Resolve currently selected email template — used by the preview dialog so
  // the admin can see exactly what cover email the customer will receive.
  const selectedEmailTemplate = emailTemplates.find((t) => t.id === emailTemplateId);

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="xero-invoice-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isQuote ? <FileText className="w-5 h-5 text-amber-600" /> : <Receipt className="w-5 h-5 text-blue-600" />}
            Send to Xero — {name}
          </DialogTitle>
          <DialogDescription>
            Create a {label.toLowerCase()} in Xero. Customer contact will be auto-created if it doesn't exist.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
          </div>
        ) : (
          <div className="space-y-5">
            {/* Quote or Invoice toggle */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Document Type</Label>
              <div className="grid grid-cols-2 gap-3" data-testid="doc-type-selector">
                {[
                  { key: 'invoice', label: 'Invoice', color: 'blue', icon: '£' },
                  { key: 'quote', label: 'Quote', color: 'amber', icon: '✎' },
                ].map(opt => (
                  <button
                    key={opt.key}
                    onClick={() => handleDocTypeChange(opt.key)}
                    className={`p-3 rounded-lg border-2 text-center transition-all font-medium ${
                      docType === opt.key
                        ? `border-${opt.color}-500 bg-${opt.color}-50 text-${opt.color}-700`
                        : 'border-slate-200 hover:border-slate-300 text-slate-600'
                    }`}
                    data-testid={`doc-type-${opt.key}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Template selector */}
            {filteredTemplates.length > 0 && (
              <div className="space-y-2">
                <Label className="text-sm font-medium">Template</Label>
                <Select value={selectedTemplate || 'none'} onValueChange={handleTemplateSelect}>
                  <SelectTrigger data-testid="xero-template-select">
                    <SelectValue placeholder="No template" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No template (manual)</SelectItem>
                    {filteredTemplates.map(t => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.name} {t.is_default ? '(Default)' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Amount Quick Select (invoices only) */}
            {!isQuote && (
              <div className="space-y-2">
                <Label className="text-sm font-medium">Amount Type</Label>
                <div className="grid grid-cols-3 gap-3" data-testid="invoice-type-selector">
                  {[
                    { key: 'deposit', label: 'Deposit', amount: depositAmount },
                    { key: 'final_payment', label: 'Final Payment', amount: finalBillAmount },
                    { key: 'custom', label: 'Custom', amount: null },
                  ].map(opt => (
                    <button
                      key={opt.key}
                      onClick={() => handleTypeChange(opt.key)}
                      className={`p-3 rounded-lg border-2 text-left transition-all ${
                        invoiceType === opt.key
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                      data-testid={`invoice-type-${opt.key}`}
                    >
                      <div className="text-sm font-medium text-slate-900">{opt.label}</div>
                      {opt.amount !== null ? (
                        <div className="text-lg font-bold text-slate-700">
                          £{opt.amount.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                        </div>
                      ) : (
                        <div className="text-sm text-slate-400">Enter below</div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Settings row */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Xero Branding Theme</Label>
                <Select value={selectedTheme || 'default'} onValueChange={(v) => setSelectedTheme(v === 'default' ? '' : v)}>
                  <SelectTrigger data-testid="xero-theme-select">
                    <SelectValue placeholder="Default template" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="default">Default</SelectItem>
                    {brandingThemes.map(theme => (
                      <SelectItem key={theme.BrandingThemeID} value={theme.BrandingThemeID}>
                        {theme.Name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">{isQuote ? 'Expiry (days)' : 'Payment Terms (days)'}</Label>
                <Input type="number" value={dueDays} onChange={(e) => setDueDays(parseInt(e.target.value) || 30)} min={1} data-testid="invoice-due-days" />
              </div>
            </div>

            {/* Reference */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Reference</Label>
              <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder={`Helios - ${name}`} data-testid="invoice-reference" />
            </div>

            {/* Default VAT / Tax type */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">VAT / Tax code (default for all lines)</Label>
              <Select value={defaultTaxType || 'account_default'} onValueChange={(v) => setDefaultTaxType(v === 'account_default' ? '' : v)}>
                <SelectTrigger data-testid="invoice-tax-type">
                  <SelectValue placeholder="Use Xero account default" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="account_default">Use Xero account default</SelectItem>
                  {taxRates.filter(t => t.tax_type).map((t) => (
                    <SelectItem key={t.tax_type} value={t.tax_type}>
                      {t.name} {t.effective_rate !== null && t.effective_rate !== undefined ? `(${t.effective_rate}%)` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-500">
                Most domestic solar work is <strong>Zero Rated (sales)</strong> under the UK VAT relief — this is the default. Pick another rate per line in Xero if you need to mix.
              </p>
            </div>

            {/* Line Items */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">Line Items</Label>
                <Button variant="outline" size="sm" onClick={addLineItem} data-testid="add-line-item-btn">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Add Line
                </Button>
              </div>
              <div className="space-y-2">
                {lineItems.map((item, idx) => (
                  <Card key={idx} className="border-slate-200">
                    <CardContent className="p-3">
                      <div className="flex items-start gap-3">
                        <div className="flex-1 space-y-2">
                          <Input value={item.description} onChange={(e) => updateLineItem(idx, 'description', e.target.value)} placeholder="Description" className="text-sm" data-testid={`line-item-desc-${idx}`} />
                          <div className="grid grid-cols-3 gap-2">
                            <div>
                              <label className="text-xs text-slate-500">Qty</label>
                              <Input type="number" value={item.quantity} onChange={(e) => updateLineItem(idx, 'quantity', e.target.value)} min={1} className="text-sm" data-testid={`line-item-qty-${idx}`} />
                            </div>
                            <div>
                              <label className="text-xs text-slate-500">Unit Price (£)</label>
                              <Input type="number" value={item.unit_amount} onChange={(e) => updateLineItem(idx, 'unit_amount', e.target.value)} step="0.01" min={0} className="text-sm" data-testid={`line-item-amount-${idx}`} />
                            </div>
                            <div>
                              <label className="text-xs text-slate-500">Account</label>
                              <Select value={item.account_code} onValueChange={(v) => updateLineItem(idx, 'account_code', v)}>
                                <SelectTrigger className="text-sm" data-testid={`line-item-account-${idx}`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {accounts.length > 0 ? accounts.map(a => (
                                    <SelectItem key={a.code} value={a.code}>{a.code} - {a.name}</SelectItem>
                                  )) : (
                                    <SelectItem value="200">200 - Sales</SelectItem>
                                  )}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1 pt-1">
                          <span className="text-sm font-semibold text-slate-700">
                            £{((parseFloat(item.unit_amount) || 0) * (parseFloat(item.quantity) || 0)).toFixed(2)}
                          </span>
                          {lineItems.length > 1 && (
                            <button onClick={() => removeLineItem(idx)} className="p-1 hover:bg-red-50 hover:text-red-500 rounded" data-testid={`remove-line-item-${idx}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Email template selector — used when Helios sends the email (quotes always; invoices when a template is picked) */}
            <div className="space-y-2 p-3 border border-amber-200 bg-amber-50 rounded-lg">
              <Label className="text-sm font-medium flex items-center gap-2">
                <Mail className="w-4 h-4 text-amber-700" />
                {isQuote
                  ? 'Email template (used when "Mark as Sent" → Helios emails the customer)'
                  : 'Email template (optional — overrides Xero auto-email with branded Helios email + PDF)'}
              </Label>
              <Select value={emailTemplateId} onValueChange={setEmailTemplateId}>
                <SelectTrigger data-testid="invoice-email-template">
                  <SelectValue placeholder="Default" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">
                    {isQuote ? 'Default Helios cover email' : 'Default — let Xero email the customer'}
                  </SelectItem>
                  {emailTemplates.filter(t => t.active !== false && t.id).map(t => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.name || t.subject || t.trigger}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {emailTemplateId !== 'default' && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="mt-1"
                  onClick={() => setEmailPreviewOpen(true)}
                  data-testid="xero-email-preview-btn"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Preview the cover email
                </Button>
              )}
              <p className="text-[11px] text-amber-700">
                {isQuote
                  ? `Xero doesn't auto-email quotes — Helios attaches the PDF and sends using your selected template (or a friendly default).`
                  : `Pick a template to send the invoice from Helios with the PDF attached. Leave as Default to let Xero send its standard email with online-pay link.`}
              </p>
            </div>

            {/* Total + VAT breakdown */}
            {(() => {
              const subtotal = total;
              const taxRate = taxRates.find(t => t.tax_type === defaultTaxType);
              const ratePct = parseFloat(taxRate?.effective_rate ?? 0);
              const vat = subtotal * (ratePct / 100);
              const grandTotal = subtotal + vat;
              return (
                <div className="p-4 bg-slate-50 rounded-lg border space-y-1.5" data-testid="invoice-totals-block">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-600">Subtotal</span>
                    <span className="font-medium text-slate-900">£{subtotal.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-600">
                      VAT {taxRate ? <span className="text-xs text-slate-400">({taxRate.name} · {ratePct}%)</span> : <span className="text-xs text-slate-400">(per Xero account default)</span>}
                    </span>
                    <span className="font-medium text-slate-900">£{vat.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex items-center justify-between pt-2 border-t border-slate-200">
                    <span className="font-semibold text-slate-900">{label} Total</span>
                    <span className="text-2xl font-bold text-slate-900 flex items-center gap-1" data-testid="invoice-total">
                      <PoundSterling className="w-5 h-5" />
                      {grandTotal.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>
              );
            })()}

            {/* Actions */}
            <div className="flex items-center justify-between pt-2 flex-wrap gap-3">
              <div className="text-xs text-slate-500">
                {isQuote
                  ? <>If new contact, it'll be created in Xero from the customer's CRM details.</>
                  : <>"Send from Xero" emails the customer the invoice with online-pay link.</>
                }
              </div>
              <div className="flex gap-3 flex-wrap">
                <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="cancel-xero-invoice" disabled={creating}>Cancel</Button>
                <Button
                  variant="outline"
                  onClick={() => handleCreate('draft')}
                  disabled={creating || total <= 0}
                  className="border-slate-300 text-slate-700 hover:bg-slate-50"
                  data-testid="save-xero-draft-btn"
                >
                  {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
                  Save as Draft
                </Button>
                <Button
                  onClick={() => handleCreate('send')}
                  disabled={creating || total <= 0}
                  className={isQuote ? 'bg-amber-600 hover:bg-amber-700 text-white' : 'bg-emerald-600 hover:bg-emerald-700 text-white'}
                  data-testid="send-xero-invoice-btn"
                >
                  {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Receipt className="w-4 h-4 mr-2" />}
                  {isQuote ? 'Mark as Sent' : 'Send from Xero'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>

    <EmailPreviewDialog
      open={emailPreviewOpen}
      onOpenChange={setEmailPreviewOpen}
      to={customer?.email}
      subject={replaceCustomerVars(selectedEmailTemplate?.subject || '', customer)}
      bodyHtml={replaceCustomerVars(selectedEmailTemplate?.body_html || '', customer)}
      recipientLabel={customer?.first_name || 'the customer'}
    />
    </>
  );
};
